<template>
  <div class="crm-integration-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="page-header">
          <h1 class="page-title">Zoho CRM Integration</h1>
          <p class="page-subtitle">Manage your Zoho CRM integration and sync settings</p>
        </div>

        <!-- Plan Restriction Banner -->
        <div v-if="!hasCRMAccess" class="plan-restriction-banner">
          <div class="restriction-icon">🔒</div>
          <div class="restriction-content">
            <h2>Upgrade Required</h2>
            <p>Zoho CRM integration is available only for <strong>Starter & Pro Plans</strong>.</p>
            <p>Your current plan: <strong>{{ userPlan }}</strong></p>
            <a href="https://api.whatsapp.com/send?phone=201206332365" target="_blank" class="upgrade-btn">
              Contact Us to Upgrade
            </a>
          </div>
        </div>

        <div v-else class="crm-content">
      <!-- Connection Status Card -->
      <div class="crm-status-card">
        <div class="status-header">
          <div class="status-logo">
            <img src="https://www.zohowebstatic.com/sites/zweb/images/commonroot/zoho-logo-web.svg"
                 alt="Zoho CRM"
                 class="zoho-logo" />
          </div>
          <div class="status-info">
            <h3>Zoho CRM Integration</h3>
            <div class="status-badge" :class="statusColor">
              <span class="status-icon">{{ statusIcon }}</span>
              <span>{{ statusText }}</span>
            </div>
          </div>
          <div class="status-actions">
            <button v-if="!crmStatus.connected"
                    @click="connectCRM"
                    :disabled="connecting"
                    class="btn btn-primary">
              <span v-if="connecting" class="loading-spinner"></span>
              {{ connecting ? 'Connecting...' : 'Connect Zoho CRM' }}
            </button>
            <button v-else
                    @click="showDisconnectModal = true"
                    class="btn btn-danger">
              Disconnect
            </button>
          </div>
        </div>

        <div v-if="crmStatus.connected" class="status-details">
          <div class="detail-item">
            <span class="detail-label">Last Sync:</span>
            <span class="detail-value">{{ formatLastSync(crmStatus.last_sync_at) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Domain:</span>
            <span class="detail-value">zoho{{ crmStatus.zoho_domain }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Token Expires:</span>
            <span class="detail-value" :class="{ 'text-danger': crmStatus.token_expired }">
              {{ formatTokenExpiry(crmStatus.token_expires_at) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Sync Settings -->
      <div v-if="crmStatus.connected" class="sync-settings-card">
        <div class="card-header">
          <h3>Sync Settings</h3>
          <p>Configure what data to automatically sync with Zoho CRM</p>
        </div>

        <div class="settings-grid">
          <div class="setting-item">
            <div class="setting-info">
              <h4>Enable CRM Sync</h4>
              <p>Master toggle for all CRM synchronization</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="syncSettings.sync_enabled" @change="updateSettings">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="setting-item" :class="{ disabled: !syncSettings.sync_enabled }">
            <div class="setting-info">
              <h4>Auto-sync Call Analysis</h4>
              <p>Automatically create contacts and notes from AI call analysis</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox"
                     v-model="syncSettings.auto_sync_calls"
                     :disabled="!syncSettings.sync_enabled"
                     @change="updateSettings">
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div class="setting-item" :class="{ disabled: !syncSettings.sync_enabled }">
            <div class="setting-info">
              <h4>Auto-sync WhatsApp Leads</h4>
              <p>Automatically create lead records from WhatsApp conversations</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox"
                     v-model="syncSettings.auto_sync_whatsapp"
                     :disabled="!syncSettings.sync_enabled"
                     @change="updateSettings">
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <!-- Sync History -->
      <div v-if="crmStatus.connected" class="sync-history-card">
        <div class="card-header">
          <h3>Sync History</h3>
          <div class="history-filters">
            <select v-model="historyFilter.sync_type" @change="loadSyncHistory" class="filter-select">
              <option value="">All Types</option>
              <option value="call_analysis">Call Analysis</option>
              <option value="whatsapp_lead">WhatsApp Leads</option>
              <option value="appointment">Appointment</option>
              <option value="manual_sync">Manual Sync</option>
            </select>
            <select v-model="historyFilter.success" @change="loadSyncHistory" class="filter-select">
              <option value="">All Status</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
        </div>

        <div class="sync-history-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>CRM Record</th>
                <th>Status</th>
                <th>Response Time</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in syncHistory.logs" :key="log.id">
                <td>
                  <span class="sync-type-badge" :class="log.sync_type">
                    {{ formatSyncType(log.sync_type) }}
                  </span>
                </td>
                <td>{{ log.crm_record_type }}</td>
                <td>
                  <span class="status-badge" :class="log.success ? 'success' : 'error'">
                    {{ log.success ? 'Success' : 'Failed' }}
                  </span>
                  <div v-if="!log.success && log.error_message" class="error-message">
                    {{ log.error_message }}
                  </div>
                </td>
                <td>{{ log.response_time_ms }}ms</td>
                <td>{{ formatDate(log.created_at) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="syncHistory.logs && syncHistory.logs.length === 0" class="empty-state">
            <p>No sync history found</p>
          </div>

          <!-- Pagination -->
          <div v-if="syncHistory.pagination && syncHistory.pagination.total_pages > 1" class="pagination">
            <button @click="changePage(syncHistory.pagination.current_page - 1)"
                    :disabled="!syncHistory.pagination.has_prev_page"
                    class="btn btn-secondary">
              Previous
            </button>
            <span class="page-info">
              Page {{ syncHistory.pagination.current_page }} of {{ syncHistory.pagination.total_pages }}
            </span>
            <button @click="changePage(syncHistory.pagination.current_page + 1)"
                    :disabled="!syncHistory.pagination.has_next_page"
                    class="btn btn-secondary">
              Next
            </button>
          </div>
        </div>
      </div>
        </div>
      </main>
    </div>

    <!-- Disconnect Confirmation Modal -->
    <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Disconnect Zoho CRM</h3>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to disconnect Zoho CRM integration?</p>
          <p class="warning-text">This will stop all automatic syncing and revoke access to your CRM data.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDisconnectModal = false" class="btn btn-secondary">Cancel</button>
          <button @click="disconnectCRM" :disabled="disconnecting" class="btn btn-danger">
            {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { CRMService } from '@/services/crmService'
import { authStore } from '@/store/auth'

export default {
  name: 'CRMIntegrationView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      crmStatus: {
        connected: false,
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false,
        token_expires_at: null,
        token_expired: false,
        last_sync_at: null,
        zoho_domain: '.com'
      },
      syncSettings: {
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false
      },
      syncHistory: {
        logs: [],
        pagination: null
      },
      historyFilter: {
        sync_type: '',
        success: '',
        page: 1
      },
      connecting: false,
      disconnecting: false,
      showDisconnectModal: false,
      loading: true
    }
  },
  computed: {
    hasCRMAccess () {
      // CRM integrations are only available for Starter and Pro plans
      const planName = authStore.user?.plan
      console.log('🔍 Zoho CRM Access Check:', {
        planName: planName,
        user: authStore.user
      })
      // Check if plan name includes "Starter" or "Pro"
      if (!planName) return false
      return planName.includes('Starter') || planName.includes('Pro')
    },
    userPlan () {
      return authStore.user?.plan || 'No Plan'
    },
    statusText () {
      if (this.crmStatus.token_expired) return 'Token Expired'
      return this.crmStatus.connected ? 'Connected' : 'Not Connected'
    },
    statusColor () {
      if (this.crmStatus.token_expired) return 'warning'
      return this.crmStatus.connected ? 'success' : 'error'
    },
    statusIcon () {
      if (this.crmStatus.token_expired) return '⚠️'
      return this.crmStatus.connected ? '✓' : '○'
    }
  },
  mounted () {
    this.loadCRMStatus()
  },
  methods: {
    async loadCRMStatus () {
      try {
        this.loading = true
        const status = await CRMService.getStatus()
        this.crmStatus = { ...this.crmStatus, ...status }

        if (status.connected) {
          this.syncSettings = {
            sync_enabled: status.sync_enabled,
            auto_sync_calls: status.auto_sync_calls,
            auto_sync_whatsapp: status.auto_sync_whatsapp
          }
          await this.loadSyncHistory()
        }
      } catch (error) {
        console.error('Error loading CRM status:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: 'Failed to load CRM status'
        })
      } finally {
        this.loading = false
      }
    },

    async connectCRM () {
      try {
        this.connecting = true
        const result = await CRMService.connect()

        if (result.auth_url) {
          window.location.href = result.auth_url
        }
      } catch (error) {
        console.error('Error connecting CRM:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to connect to Zoho CRM'
        })
      } finally {
        this.connecting = false
      }
    },

    async disconnectCRM () {
      try {
        this.disconnecting = true
        await CRMService.disconnect()

        this.crmStatus.connected = false
        this.showDisconnectModal = false

        this.$emit('show-toast', {
          type: 'success',
          message: 'Zoho CRM disconnected successfully'
        })
      } catch (error) {
        console.error('Error disconnecting CRM:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to disconnect CRM'
        })
      } finally {
        this.disconnecting = false
      }
    },

    async updateSettings () {
      try {
        await CRMService.updateSettings(this.syncSettings)
        this.$emit('show-toast', {
          type: 'success',
          message: 'Settings updated successfully'
        })
      } catch (error) {
        console.error('Error updating settings:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to update settings'
        })
        // Reload status to revert changes
        await this.loadCRMStatus()
      }
    },

    async loadSyncHistory () {
      try {
        const params = {
          page: this.historyFilter.page,
          per_page: 20
        }

        if (this.historyFilter.sync_type) {
          params.sync_type = this.historyFilter.sync_type
        }

        if (this.historyFilter.success !== '') {
          params.success = this.historyFilter.success
        }

        const history = await CRMService.getSyncHistory(params)
        this.syncHistory = history
      } catch (error) {
        console.error('Error loading sync history:', error)
      }
    },

    async changePage (page) {
      this.historyFilter.page = page
      await this.loadSyncHistory()
    },

    formatLastSync (timestamp) {
      return CRMService.formatLastSync(timestamp)
    },

    formatTokenExpiry (timestamp) {
      if (!timestamp) return 'Unknown'
      const date = new Date(timestamp)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },

    formatSyncType (type) {
      const types = {
        call_analysis: 'Call Analysis',
        whatsapp_lead: 'WhatsApp Lead',
        appointment: 'Appointment',
        manual_sync: 'Manual Sync',
        customer_data: 'Customer Data'
      }
      return types[type] || type
    },

    formatDate (timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }
  }
}
</script>

<style scoped>
.crm-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.crm-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Status Card */
.crm-status-card {
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
}

.status-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
}

.status-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid var(--border-secondary);
}

.zoho-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.status-info {
  flex: 1;
}

.status-info h3 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.status-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.status-badge.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.status-actions {
  display: flex;
  gap: 12px;
}

.status-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid var(--border-secondary);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.detail-value.text-danger {
  color: #ef4444;
}

/* Settings Card */
.sync-settings-card,
.sync-history-card {
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-secondary);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-header p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.history-filters {
  display: flex;
  gap: 12px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
}

.settings-grid {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 8px;
  transition: opacity 0.2s;
}

.setting-item.disabled {
  opacity: 0.5;
}

.setting-info h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.setting-info p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-primary);
  transition: 0.2s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #3b82f6;
}

input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* History Table */
.sync-history-table {
  padding: 24px;
}

.sync-history-table table {
  width: 100%;
  border-collapse: collapse;
}

.sync-history-table th,
.sync-history-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-secondary);
}

.sync-history-table th {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sync-history-table td {
  color: var(--text-primary);
  font-size: 14px;
}

.sync-type-badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.error-message {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-secondary);
}

.page-info {
  font-size: 14px;
  color: var(--text-secondary);
}

/* Buttons */
.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

/* Loading Spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: var(--bg-card);
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--shadow-lg);
}

.modal-header {
  padding: 20px 24px 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-body {
  padding: 16px 24px;
}

.modal-body p {
  margin: 0 0 12px;
  color: var(--text-primary);
}

.warning-text {
  color: var(--text-secondary);
  font-size: 14px;
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* Plan Restriction Banner */
.plan-restriction-banner {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: white;
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
  max-width: 600px;
  margin: 40px auto;
}

.restriction-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.restriction-content h2 {
  margin: 0 0 16px;
  font-size: 28px;
  font-weight: 700;
}

.restriction-content p {
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.6;
}

.restriction-content strong {
  font-weight: 700;
  text-decoration: underline;
}

.upgrade-btn {
  display: inline-block;
  margin-top: 24px;
  padding: 14px 28px;
  background: white;
  color: #d97706;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.upgrade-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  background: #fef3c7;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .page-content {
    padding: 16px;
  }

  .plan-restriction-banner {
    padding: 24px;
    margin: 24px 16px;
  }

  .restriction-icon {
    font-size: 48px;
  }

  .restriction-content h2 {
    font-size: 22px;
  }

  .restriction-content p {
    font-size: 14px;
  }

  .status-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .status-details {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
  }

  .history-filters {
    width: 100%;
  }

  .filter-select {
    flex: 1;
  }

  .sync-history-table {
    overflow-x: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
